import i18n from 'i18next'
import {
  CountryCode,
  ParseError,
  parsePhoneNumberWithError,
} from 'libphonenumber-js'
import { initReactI18next } from 'react-i18next'
import { en } from '~/locales/en'

const getPotentialCountryCode = (lng?: string): CountryCode | undefined => {
  if (lng) {
    const parts = lng.split(/[-_]/)
    if (parts.length > 1) {
      const potentialCountry = parts[parts.length - 1]

      if (potentialCountry.length === 2) {
        return potentialCountry.toUpperCase() as CountryCode
      }
    }

    if (lng.length === 2 && lng === lng.toUpperCase()) {
      return lng as CountryCode
    }
  }

  return undefined
}

/**
 * Maps locale to currency code based on country/region
 * This is not a great system, but since we don't have a better way to get the currency code from the integration data, this will do.
 */
export const getPotentialCurrencyCode = (locale?: string): string => {
  if (!locale) return 'USD'

  // Extract country code from locale (e.g., 'en-US' -> 'US', 'fr-CA' -> 'CA')
  const parts = locale.split(/[-_]/)
  const countryCode =
    parts.length > 1 ? parts[parts.length - 1].toUpperCase() : undefined

  // Map country codes to currency codes
  const currencyMap: Record<string, string> = {
    US: 'USD',
    GB: 'GBP',
    CA: 'CAD',
    MX: 'MXN',
    KR: 'KRW',
    NP: 'NPR',
    IN: 'INR',
  }

  // If we have a country code, use it; otherwise check language code
  if (countryCode && currencyMap[countryCode]) {
    return currencyMap[countryCode]
  }

  // Fallback: map language codes to default currencies
  const languageCode = parts[0].toLowerCase()
  const languageCurrencyMap: Record<string, string> = {
    ko: 'KRW',
    ne: 'NPR',
    te: 'INR',
  }

  return languageCurrencyMap[languageCode] || 'USD'
}

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: { en },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
  })

i18n.services.formatter?.add('phone', (value, lng) => {
  const defaultCountry = getPotentialCountryCode(lng)
  const stringValue = String(value)

  try {
    const phoneNumber = parsePhoneNumberWithError(stringValue, {
      defaultCountry,
    })

    return phoneNumber.formatInternational()
  } catch (error: unknown) {
    const contextCountry = defaultCountry || 'none'

    if (error instanceof ParseError) {
      console.error(
        `Phone number parsing error for "${stringValue}" (country context: ${contextCountry}): ${error.message}`,
      )
    } else if (error instanceof Error) {
      console.error(
        `Generic error formatting phone number "${stringValue}" (country context: ${contextCountry}): ${error.message}`,
      )
    } else {
      console.error(
        `Unknown error formatting phone number "${stringValue}" (country context: ${contextCountry}):`,
        error,
      )
    }
    return stringValue
  }
})

i18n.services.formatter?.add('currency', (value, lng) => {
  const numericValue =
    typeof value === 'string' ? Number.parseFloat(value) : value

  if (Number.isNaN(numericValue)) return ''

  const currency = getPotentialCurrencyCode(lng)
  return new Intl.NumberFormat(lng, {
    style: 'currency',
    currency,
  }).format(numericValue)
})

/** Converts an i18next language code (`en-US`, `te_IN`) to a BCP 47 tag for `<html lang>`. */
export const toDocumentLanguage = (lng?: string): string => {
  if (!lng || lng === 'cimode') return 'en'
  try {
    return Intl.getCanonicalLocales(lng.replace(/_/g, '-'))[0] ?? 'en'
  } catch {
    return 'en'
  }
}

/** Keeps `<html lang>` on the language the UI is actually rendered in. */
export const syncDocumentLanguage = () => {
  if (typeof document === 'undefined') return
  document.documentElement.lang = toDocumentLanguage(
    i18n.resolvedLanguage ?? i18n.language,
  )
}

i18n.on('languageChanged', syncDocumentLanguage)
syncDocumentLanguage()

export const i18nConfig = i18n
